import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "../AuthProvider";
import {
  deleteCampaign,
  downloadCampaignCreative,
  fetchCampaignCars,
  fetchCampaignRequests,
  fetchCampaignLocations,
  fetchCampaigns,
  updateCampaignStatus,
  updateCampaignCarStatus,
  updateCampaignRequestStatus,
} from "../services/campaignService";

const statusLabels = {
  pending: "Ausstehend",
  approved: "Freigegeben",
  rejected: "Abgelehnt",
};

const statusTabs = [
  { value: "pending", label: "Ausstehend" },
  { value: "approved", label: "Freigegeben" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "all", label: "Alle" },
];

const CAPTURE_BASE_URL = "https://capture.werbeauto.ch";

const getAdvertiserPortalBaseUrl = () => {
  const configuredUrl = process.env.REACT_APP_ADVERTISER_PORTAL_URL?.trim();
  if (configuredUrl) return configuredUrl.replace(/\/$/, "");

  const hostname = window.location.hostname;
  const isLocalDevelopment = ["localhost", "127.0.0.1", "::1"].includes(hostname);
  if (isLocalDevelopment) {
    return `${window.location.protocol}//${hostname}:3001`;
  }

  if (hostname.startsWith("admin.")) {
    return `${window.location.protocol}//advertiser.${hostname.slice("admin.".length)}`;
  }

  return "https://advertiser.werbeauto.ch";
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parts = String(value).split("/");
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map(Number);
  if (!dd || !mm || !yyyy) return null;
  return new Date(yyyy, (mm || 1) - 1, dd || 1, 0, 0, 0, 0);
};

const isAssignmentActive = (startDate, endDate) => {
  const start = parseDateValue(startDate);
  const end = parseDateValue(endDate);
  if (!start) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (start > today) return false;
  if (!end) return true;
  return end >= today;
};

const CampaignBadge = ({ status }) => (
  <span className={`campaign-pill campaign-pill-${status}`}>
    {statusLabels[status] ?? status ?? "Unbekannt"}
  </span>
);

const DEFAULT_CENTER = [47.3769, 8.5417];

const buildCaptureUrl = (assignmentId) =>
  assignmentId ? `${CAPTURE_BASE_URL}/${assignmentId}` : null;

const normalizeForFilename = (value) => {
  if (!value) return "";
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
};

const buildDownloadName = (driverName, driverAddress, fallback) => {
  const safeDriver = normalizeForFilename(driverName);
  const safeAddress = normalizeForFilename(driverAddress);
  const base =
    [safeDriver, safeAddress].filter(Boolean).join("_") ||
    normalizeForFilename(fallback);
  return base ? `${base}.svg` : "capture-qr.svg";
};

const creativeExtensionForType = (contentType) => {
  if (contentType === "image/png") {
    return ".png";
  }
  return ".svg";
};

const buildCreativeFilename = (title, fallbackId, contentType) => {
  const extension = creativeExtensionForType(contentType);
  const safeTitle = normalizeForFilename(title);
  if (safeTitle) {
    return `${safeTitle}-creative${extension}`;
  }
  const fallbackSafe = normalizeForFilename(fallbackId);
  return fallbackSafe ? `${fallbackSafe}-creative${extension}` : `campaign-creative${extension}`;
};

const addressToString = (address = {}) => {
  const { street, house_number, postal_code, city } = address;
  const parts = [];
  if (street || house_number) {
    parts.push([street, house_number].filter(Boolean).join(" "));
  }
  if (postal_code || city) {
    parts.push([postal_code, city].filter(Boolean).join(" "));
  }
  return parts.length ? parts.join(", ") : "Adresse unbekannt";
};

export default function KampagnenPage() {
  const { fetchWithAuth } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState([]);
  const [campaignCars, setCampaignCars] = useState({});
  const [campaignRequests, setCampaignRequests] = useState({});
  const [requestsLoading, setRequestsLoading] = useState({});
  const [requestErrors, setRequestErrors] = useState({});
  const [requestStatusUpdating, setRequestStatusUpdating] = useState({});
  const [campaignDetailTab, setCampaignDetailTab] = useState({});
  const [carsLoading, setCarsLoading] = useState({});
  const [carErrors, setCarErrors] = useState({});
  const [carStatusUpdating, setCarStatusUpdating] = useState({});
  const [mapCampaignId, setMapCampaignId] = useState(null);
  const [locationCache, setLocationCache] = useState({});
  const [locationLoading, setLocationLoading] = useState({});
  const [locationErrors, setLocationErrors] = useState({});
  const [creativeLoading, setCreativeLoading] = useState({});
  const [creativeErrors, setCreativeErrors] = useState({});
  const qrRefs = useRef({});

  const registerQrRef = useCallback(
    (assignmentId) => (node) => {
      if (!assignmentId) return;
      if (node) {
        qrRefs.current[assignmentId] = node.querySelector("svg") ?? null;
      } else {
        delete qrRefs.current[assignmentId];
      }
    },
    []
  );

  const handleDownloadQr = useCallback((assignmentId, fileName) => {
    if (!assignmentId) return;
    const svgElement = qrRefs.current[assignmentId];
    if (!svgElement) return;

    const cloned = svgElement.cloneNode(true);
    cloned.setAttribute("width", "1000");
    cloned.setAttribute("height", "1000");
    const existingViewBox = cloned.getAttribute("viewBox");
    if (!existingViewBox) {
      const fallbackSize =
        parseFloat(svgElement.getAttribute("width")) ||
        parseFloat(svgElement.style?.width) ||
        256;
      cloned.setAttribute("viewBox", `0 0 ${fallbackSize} ${fallbackSize}`);
    }
    if (!cloned.getAttribute("xmlns")) {
      cloned.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    }

    const serializer = new XMLSerializer();
    const svgContent = serializer.serializeToString(cloned);
    const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName ?? `car-${assignmentId}-qr.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleDownloadCreative = useCallback(
    async (campaign) => {
      if (!campaign?.id) return;
      const campaignId = campaign.id;
      setCreativeLoading((prev) => ({ ...prev, [campaignId]: true }));
      setCreativeErrors((prev) => ({ ...prev, [campaignId]: null }));
      try {
        const blob = await downloadCampaignCreative(fetchWithAuth, campaignId);
        const targetFilename = buildCreativeFilename(campaign.title, campaignId, blob.type);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = targetFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        setCreativeErrors((prev) => ({
          ...prev,
          [campaignId]: err?.message || "Creative konnte nicht heruntergeladen werden.",
        }));
      } finally {
        setCreativeLoading((prev) => ({ ...prev, [campaignId]: false }));
      }
    },
    [fetchWithAuth]
  );

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCampaigns(fetchWithAuth);
      setCampaigns(data);
    } catch (err) {
      setError(err.message || "Kampagnen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const countsByStatus = useMemo(() => {
    const initial = { pending: 0, approved: 0, rejected: 0 };
    for (const campaign of campaigns) {
      const key = campaign.approval_status;
      if (key && key in initial) {
        initial[key] += 1;
      }
    }
    return initial;
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    if (statusFilter === "all") return campaigns;
    return campaigns.filter((c) => c.approval_status === statusFilter);
  }, [campaigns, statusFilter]);

  const handleStatusChange = async (campaign, nextStatus) => {
    const actionLabel =
      nextStatus === "approved" ? "freigeben" : "ablehnen";
    const confirm = window.confirm(
      `Möchtest du die Kampagne "${campaign.title}" ${actionLabel}?`
    );
    if (!confirm) return;

    setUpdatingId(campaign.id);
    setError(null);
    try {
      const updated = await updateCampaignStatus(
        fetchWithAuth,
        campaign.id,
        nextStatus
      );
      if (updated) {
        setCampaigns((existing) =>
          existing.map((c) => (c.id === updated.id ? updated : c))
        );
      }
    } catch (err) {
      setError(err.message || "Status konnte nicht aktualisiert werden.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Möchtest du die Kampagne "${campaign.title}" endgültig löschen?`)) {
      return;
    }
    setUpdatingId(campaign.id);
    setError(null);
    try {
      await deleteCampaign(fetchWithAuth, campaign.id);
      setCampaigns((existing) => existing.filter((entry) => entry.id !== campaign.id));
    } catch (err) {
      setError(err.message || "Kampagne konnte nicht gelöscht werden.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCarStatusChange = async (campaignId, assignmentId, nextStatus) => {
    const actionLabel = nextStatus === "approved" ? "akzeptieren" : "ablehnen";
    const confirm = window.confirm(`Möchtest du das Fahrzeug ${actionLabel}?`);
    if (!confirm) return;

    const previousCar =
      (campaignCars[campaignId] || []).find((c) => c.assignment_id === assignmentId) || null;
    const previousStatus = previousCar?.approval_status;
    const wasActive =
      previousStatus === "approved" &&
      isAssignmentActive(previousCar?.start_date, previousCar?.end_date);

    setCarStatusUpdating((prev) => ({ ...prev, [assignmentId]: true }));
    setCarErrors((prev) => ({ ...prev, [campaignId]: null }));
    try {
      const updated = await updateCampaignCarStatus(
        fetchWithAuth,
        campaignId,
        assignmentId,
        nextStatus
      );
      if (updated) {
        setCampaignCars((prev) => {
          const list = prev[campaignId] || [];
          return {
            ...prev,
            [campaignId]: list.map((car) =>
              car.assignment_id === assignmentId ? { ...car, ...updated } : car
            ),
          };
        });
        const willBeActive =
          nextStatus === "approved" &&
          isAssignmentActive(
            updated?.start_date || previousCar?.start_date,
            updated?.end_date || previousCar?.end_date
          );
        setCampaigns((existing) =>
          existing.map((entry) => {
            if (entry.id !== campaignId) return entry;
            const next = { ...entry };
            if (previousStatus === "pending") {
              next.pending_cars = Math.max((next.pending_cars ?? 0) - 1, 0);
            }
            if (previousStatus === "approved") {
              next.approved_cars = Math.max((next.approved_cars ?? 0) - 1, 0);
              if (wasActive) {
                next.active_cars = Math.max((next.active_cars ?? 0) - 1, 0);
              }
            }
            if (nextStatus === "pending") {
              next.pending_cars = (next.pending_cars ?? 0) + 1;
            }
            if (nextStatus === "approved") {
              next.approved_cars = (next.approved_cars ?? 0) + 1;
              if (willBeActive) {
                next.active_cars = (next.active_cars ?? 0) + 1;
              }
            }
            return next;
          })
        );
      }
    } catch (err) {
      setCarErrors((prev) => ({
        ...prev,
        [campaignId]: err?.message || "Fahrzeugstatus konnte nicht geändert werden.",
      }));
    } finally {
      setCarStatusUpdating((prev) => ({ ...prev, [assignmentId]: false }));
    }
  };

  const toggleCars = (campaignId) => {
    setExpandedCampaigns((prev) => {
      const exists = prev.includes(campaignId);
      if (exists) {
        return prev.filter((id) => id !== campaignId);
      }
      // load on demand
      if (!campaignCars[campaignId] && !carsLoading[campaignId]) {
        loadCars(campaignId);
      }
      if (!campaignRequests[campaignId] && !requestsLoading[campaignId]) {
        loadRequests(campaignId);
      }
      return [...prev, campaignId];
    });
  };

  const loadRequests = useCallback(async (campaignId) => {
    setRequestsLoading((prev) => ({ ...prev, [campaignId]: true }));
    setRequestErrors((prev) => ({ ...prev, [campaignId]: null }));
    try {
      const requests = await fetchCampaignRequests(fetchWithAuth, campaignId);
      setCampaignRequests((prev) => ({ ...prev, [campaignId]: requests }));
    } catch (err) {
      setRequestErrors((prev) => ({
        ...prev,
        [campaignId]: err?.message || "Anfragen konnten nicht geladen werden.",
      }));
    } finally {
      setRequestsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  }, [fetchWithAuth]);

  const handleRequestStatusChange = async (campaignId, requestId, nextStatus) => {
    const actionLabel = nextStatus === "approved" ? "akzeptieren" : "ablehnen";
    if (!window.confirm(`Möchtest du die Anfrage ${actionLabel}?`)) return;
    setRequestStatusUpdating((prev) => ({ ...prev, [requestId]: true }));
    setRequestErrors((prev) => ({ ...prev, [campaignId]: null }));
    try {
      await updateCampaignRequestStatus(fetchWithAuth, campaignId, requestId, nextStatus);
      await Promise.all([loadRequests(campaignId), loadCars(campaignId), loadCampaigns()]);
    } catch (err) {
      setRequestErrors((prev) => ({
        ...prev,
        [campaignId]: err?.message || "Anfrage konnte nicht aktualisiert werden.",
      }));
    } finally {
      setRequestStatusUpdating((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const loadCars = async (campaignId) => {
    setCarsLoading((prev) => ({ ...prev, [campaignId]: true }));
    setCarErrors((prev) => ({ ...prev, [campaignId]: null }));
    try {
      const cars = await fetchCampaignCars(fetchWithAuth, campaignId);
      setCampaignCars((prev) => ({ ...prev, [campaignId]: cars }));
    } catch (err) {
      setCarErrors((prev) => ({
        ...prev,
        [campaignId]: err.message || "Fahrzeuge konnten nicht geladen werden.",
      }));
    } finally {
      setCarsLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  const openMap = (campaignId) => {
    setMapCampaignId(campaignId);
    if (!locationCache[campaignId] && !locationLoading[campaignId]) {
      loadLocations(campaignId);
    }
  };

  const closeMap = () => setMapCampaignId(null);

  const mapCampaign = mapCampaignId
    ? campaigns.find((c) => c.id === mapCampaignId)
    : null;
  const mapLocations = mapCampaignId ? locationCache[mapCampaignId] || [] : [];
  const mapLoading = mapCampaignId ? locationLoading[mapCampaignId] : false;
  const mapError = mapCampaignId ? locationErrors[mapCampaignId] : null;

  const computeCenter = (locations) => {
    if (!locations.length) return DEFAULT_CENTER;
    const lat =
      locations.reduce((sum, loc) => sum + (loc.latitude || 0), 0) /
      locations.length;
    const lon =
      locations.reduce((sum, loc) => sum + (loc.longitude || 0), 0) /
      locations.length;
    return [lat, lon];
  };

  const computeZoom = (locations) => {
    if (!locations.length) return 10;
    const maxRadius = Math.max(...locations.map((l) => l.radius || 0));
    if (maxRadius <= 250) return 14;
    if (maxRadius <= 500) return 13;
    if (maxRadius <= 1000) return 12;
    if (maxRadius <= 2000) return 11;
    if (maxRadius <= 4000) return 10;
    return 9;
  };

  const CampaignMapView = ({ locations, center, zoom }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const circlesLayerRef = useRef(null);

    useEffect(() => {
      if (!containerRef.current || mapRef.current) return undefined;

      const map = L.map(containerRef.current, {
        center,
        zoom,
        zoomControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap",
      }).addTo(map);

      const circlesLayer = L.layerGroup().addTo(map);

      mapRef.current = map;
      circlesLayerRef.current = circlesLayer;

      return () => {
        circlesLayer.remove();
        map.remove();
        mapRef.current = null;
        circlesLayerRef.current = null;
      };
    }, [center, zoom]);

    useEffect(() => {
      if (!mapRef.current) return;
      mapRef.current.setView(center, zoom);
    }, [center, zoom]);

    useEffect(() => {
      if (!circlesLayerRef.current) return;
      circlesLayerRef.current.clearLayers();
      locations.forEach((loc) => {
        if (
          loc.latitude == null ||
          loc.longitude == null ||
          typeof loc.radius !== "number"
        ) {
          return;
        }
        L.circle([loc.latitude, loc.longitude], {
          radius: loc.radius,
          color: "#0f8a6d",
          fillColor: "#0f8a6d",
          fillOpacity: 0.2,
        }).addTo(circlesLayerRef.current);
      });
    }, [locations]);

    return <div ref={containerRef} style={{ height: 360, width: "100%" }} />;
  };

  const loadLocations = async (campaignId) => {
    setLocationLoading((prev) => ({ ...prev, [campaignId]: true }));
    setLocationErrors((prev) => ({ ...prev, [campaignId]: null }));
    try {
      const locations = await fetchCampaignLocations(fetchWithAuth, campaignId);
      setLocationCache((prev) => ({ ...prev, [campaignId]: locations }));
    } catch (err) {
      setLocationErrors((prev) => ({
        ...prev,
        [campaignId]: err.message || "Gebiete konnten nicht geladen werden.",
      }));
    } finally {
      setLocationLoading((prev) => ({ ...prev, [campaignId]: false }));
    }
  };

  return (
    <>
      <div className="portal-section campaign-panel">
      <header className="campaign-top">
        <div>
          <p className="campaign-eyebrow">Anzeigenkampagnen</p>
          <h1 className="portal-title">Kampagnen</h1>
          <p className="portal-desc">
            Prüfe eingereichte Kampagnen, bestätige sie für die Plattform und
            behalte laufende Fahrzeuge im Blick.
          </p>
        </div>
        <div className="campaign-tabs">
          {statusTabs.map((tab) => {
            const count =
              tab.value === "all" ? campaigns.length : countsByStatus[tab.value] ?? 0;
            return (
              <button
                key={tab.value}
                className={`campaign-tab${
                  statusFilter === tab.value ? " active" : ""
                }`}
                onClick={() => setStatusFilter(tab.value)}
              >
                {tab.label}
                <span className="campaign-tab-count">{count}</span>
              </button>
            );
          })}
        </div>
      </header>

      {error && <div className="car-alert error">{error}</div>}

      {loading ? (
        <div className="car-empty">Kampagnen werden geladen…</div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="car-empty">Keine Kampagnen im ausgewählten Status.</div>
      ) : (
        filteredCampaigns.map((campaign) => {
          const expanded = expandedCampaigns.includes(campaign.id);
          const cars = campaignCars[campaign.id] || [];
          const carsAreLoading = carsLoading[campaign.id];
          const carsError = carErrors[campaign.id];
          const requests = campaignRequests[campaign.id] || [];
          const requestsAreLoading = requestsLoading[campaign.id];
          const requestsError = requestErrors[campaign.id];
          const detailTab = campaignDetailTab[campaign.id] || "requests";
          const paymentSubject = (campaign.id || "").slice(0, 8).toUpperCase();
          return (
            <div key={campaign.id} className="campaign-card">
              <div className="campaign-panel-top">
                <div>
                  <div className="campaign-title">{campaign.title}</div>
                  <div className="campaign-meta">
                    <span>{campaign.advertiser_name ?? "Unbekannter Werber"}</span>
                    {campaign.advertiser_city && (
                      <span> · {campaign.advertiser_city}</span>
                    )}
                  </div>
                  <div className="campaign-dates">
                    {formatDate(campaign.start_date)} – {formatDate(campaign.end_date)}
                  </div>
                  <div className="campaign-budget">
                    Budget:{" "}
                    {typeof campaign.budget === "number"
                      ? `${campaign.budget.toLocaleString("de-CH")} CHF`
                      : "—"}{" "}
                    · Fahrzeuge aktiv: {campaign.active_cars ?? 0}
                    {" · "}Anfragen: {campaign.pending_cars ?? 0}
                  </div>
                  <div className="campaign-payment">
                    Zahlungszweck:{" "}
                    {paymentSubject ? <code>{paymentSubject}</code> : "—"}
                  </div>
                </div>
                <CampaignBadge status={campaign.approval_status} />
              </div>

              <div className="campaign-actions">
                <button
                  className="btn btn-approve"
                  disabled={
                    updatingId === campaign.id ||
                    campaign.approval_status === "approved"
                  }
                  onClick={() => handleStatusChange(campaign, "approved")}
                >
                  Freigeben
                </button>
                <button
                  className="btn btn-reject"
                  disabled={
                    updatingId === campaign.id ||
                    campaign.approval_status === "rejected"
                  }
                  onClick={() => handleStatusChange(campaign, "rejected")}
                >
                  Ablehnen
                </button>
                <button
                  className="campaign-toggle"
                  onClick={() => toggleCars(campaign.id)}
                >
                  {expanded ? "Fahrzeuge ausblenden" : "Fahrzeuge anzeigen"}
                </button>
                <button
                  className="campaign-toggle"
                  onClick={() => openMap(campaign.id)}
                >
                  Karte anzeigen
                </button>
                <button
                  className="btn btn-dl"
                  type="button"
                  disabled={creativeLoading[campaign.id]}
                  onClick={() => handleDownloadCreative(campaign)}
                >
                  {creativeLoading[campaign.id]
                    ? "Creative wird geladen…"
                    : "Creative herunterladen"}
                </button>
                <a
                  className="btn btn-approve campaign-edit-link"
                  href={`${getAdvertiserPortalBaseUrl()}/campaigns/edit/${campaign.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Im Werberportal bearbeiten
                </a>
                <button
                  className="btn btn-reject"
                  disabled={updatingId === campaign.id}
                  onClick={() => handleDeleteCampaign(campaign)}
                >
                  Löschen
                </button>
              </div>
              {creativeErrors[campaign.id] && (
                <div className="car-alert error">{creativeErrors[campaign.id]}</div>
              )}

              {expanded && (
                <div className="campaign-cars">
                  <div className="campaign-actions">
                    <button
                      className="campaign-toggle"
                      onClick={() => setCampaignDetailTab((prev) => ({ ...prev, [campaign.id]: "requests" }))}
                    >
                      Fahrer-Anfragen ({requests.filter((request) => request.status === "pending").length})
                    </button>
                    <button
                      className="campaign-toggle"
                      onClick={() => setCampaignDetailTab((prev) => ({ ...prev, [campaign.id]: "qr" }))}
                    >
                      QR-Codes / Fahrer
                    </button>
                  </div>
                  {detailTab === "requests" && (
                    requestsAreLoading ? (
                      <div className="campaign-cars-empty">Anfragen werden geladen…</div>
                    ) : requestsError ? (
                      <div className="campaign-cars-empty">{requestsError}</div>
                    ) : requests.length === 0 ? (
                      <div className="campaign-cars-empty">Noch keine Fahrer-Anfragen.</div>
                    ) : (
                      requests.map((request) => (
                        <div key={request.request_id} className="campaign-car-row">
                          <div>
                            <div className="car-strong">{request.driver_name || "Unbekannter Fahrer"}</div>
                            <div className="car-muted">
                              {[request.manufacturer, request.model, request.number_plate].filter(Boolean).join(" · ")}
                            </div>
                            <CampaignBadge status={request.status} />
                          </div>
                          <div>
                            <div className="car-strong">
                              Fahrprofil: {request.match_percent == null ? "—" : `${request.match_percent}%`}
                            </div>
                            <div className="car-muted">
                              Fahrzeug: {request.fuel_compatible ? "Passt zur Kampagne" : "Passt nicht zur Kampagne"}
                            </div>
                          </div>
                          <div className="car-muted">
                            {request.start_date} – {request.end_date || "laufend"}
                          </div>
                          {request.status === "pending" && (
                            <div className="campaign-car-actions">
                              <button
                                type="button"
                                className="btn btn-approve btn-small"
                                disabled={requestStatusUpdating[request.request_id]}
                                onClick={() => handleRequestStatusChange(campaign.id, request.request_id, "approved")}
                              >
                                QR zuweisen & akzeptieren
                              </button>
                              <button
                                type="button"
                                className="btn btn-reject btn-small"
                                disabled={requestStatusUpdating[request.request_id]}
                                onClick={() => handleRequestStatusChange(campaign.id, request.request_id, "rejected")}
                              >
                                Ablehnen
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    )
                  )}
                  {detailTab === "qr" && (
                <div className="campaign-cars">
                  {carsAreLoading ? (
                    <div className="campaign-cars-empty">Fahrzeuge werden geladen…</div>
                  ) : carsError ? (
                    <div className="campaign-cars-empty">{carsError}</div>
                  ) : cars.length === 0 ? (
                    <div className="campaign-cars-empty">
                      Noch keine Fahrzeuge zugeordnet.
                    </div>
                  ) : (
                    cars.map((car) => {
                      const captureUrl = buildCaptureUrl(car.assignment_id);
                      const hasCar = Boolean(car.car_id);
                      const driverDisplay = hasCar ? (car.driver_name ?? "Unbekannter Fahrer") : "Reserviert";
                      const addressDisplay = hasCar ? addressToString(car.address) : "Wartet auf Zuordnung";
                      const downloadName = buildDownloadName(
                        driverDisplay,
                        addressDisplay,
                        car.assignment_id
                      );
                      const carStatus = car.approval_status || "pending";
                      const statusIsPending = carStatus === "pending";
                      const statusUpdating = carStatusUpdating[car.assignment_id];
                      return (
                        <div key={car.assignment_id} className="campaign-car-row">
                          <div>
                            <div className="car-strong">
                              {hasCar ? `${car.manufacturer} ${car.model}` : "Reservierter QR-Slot"}
                            </div>
                            <div className="car-muted">
                              {hasCar ? car.number_plate : "Noch kein Auto zugewiesen"}
                            </div>
                            <div className="campaign-car-status">
                              <CampaignBadge status={carStatus} />
                              {!hasCar && <span className="campaign-pill campaign-pill-pending">Reserviert</span>}
                              {statusIsPending && hasCar && (
                                <div className="campaign-car-actions">
                                  <button
                                    type="button"
                                    className="btn btn-approve btn-small"
                                    disabled={statusUpdating}
                                    onClick={() =>
                                      handleCarStatusChange(
                                        campaign.id,
                                        car.assignment_id,
                                        "approved"
                                      )
                                    }
                                  >
                                    Akzeptieren
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-reject btn-small"
                                    disabled={statusUpdating}
                                    onClick={() =>
                                      handleCarStatusChange(
                                        campaign.id,
                                        car.assignment_id,
                                        "rejected"
                                      )
                                    }
                                  >
                                    Ablehnen
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="car-strong">{driverDisplay}</div>
                            <div className="car-muted">{addressDisplay}</div>
                          </div>
                          <div className="car-muted">
                            {car.start_date} – {car.end_date ?? "laufend"}
                          </div>
                          <div className="car-code">
                            <div className="car-code-meta">
                              <div className="car-strong">{hasCar ? "Capture URL" : "Reservierter Capture URL"}</div>
                              {captureUrl ? (
                                <a
                                  href={captureUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="car-code-link"
                                >
                                  {captureUrl}
                                </a>
                              ) : (
                                <div className="car-muted">Nicht verfügbar</div>
                              )}
                              {captureUrl && (
                                <button
                                  type="button"
                                  className="car-code-dl"
                                  onClick={() =>
                                    handleDownloadQr(car.assignment_id, downloadName)
                                  }
                                >
                                  {hasCar ? "QR-Code herunterladen" : "Reservierten QR-Code herunterladen"}
                                </button>
                              )}
                            </div>
                            {captureUrl && (
                              <div
                                className="car-code-qr"
                                aria-hidden="true"
                                ref={registerQrRef(car.assignment_id)}
                              >
                                <QRCode
                                  value={captureUrl}
                                  size={84}
                                  bgColor="#ffffff"
                                  fgColor="#0f4a3f"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
      </div>
      {mapCampaign && (
        <div className="campaign-map-backdrop" onClick={closeMap}>
          <div className="campaign-map-modal" onClick={(event) => event.stopPropagation()}>
            <div className="campaign-map-top">
              <div>
                <div className="campaign-map-title">Werbegebiete</div>
                <div className="campaign-map-subtitle">{mapCampaign.title}</div>
              </div>
              <button className="campaign-toggle" onClick={closeMap}>
                Schließen
              </button>
            </div>
            <div className="campaign-map-body">
              {mapLoading ? (
                <div className="campaign-cars-empty">Gebiete werden geladen…</div>
              ) : mapError ? (
                <div className="campaign-cars-empty">{mapError}</div>
              ) : mapLocations.length === 0 ? (
                <div className="campaign-cars-empty">Keine Zielgebiete definiert.</div>
              ) : (
                <CampaignMapView
                  locations={mapLocations}
                  center={computeCenter(mapLocations)}
                  zoom={computeZoom(mapLocations)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
